import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, Injector, afterNextRender, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { Observable, filter } from 'rxjs';

import { EventType } from '@app/shared/constants-server';
import type { RentalInterruption } from '@app/shared/_models/sales';
import type { Day, RentalBillingParams, RentalLineView } from '../rental-billing.model';
import {
  CARD_FLAG_LABELS, CLOSED_LINE, END_RULE_LABELS, INTERRUPTED_BADGE, INTERRUPTION_RULE_LABELS, OVERBILLING_LABELS, PRICE_SOURCE_LABELS, closureTip, lineClosed, rentalSubtitle, rentalTitle,
  showInterruptedBadge, statusClass, statusLabel,
} from '../rental-billing.labels';
import { RentalDecisionDialogsService } from '../rental-decision-dialogs.service';
import type { CancelTarget, DecisionDialogResult, LineSheetDialogData, RangeEditTarget } from '../rental-dialogs.contract';
import type { RentalLifecycleStore } from '../rental-lifecycle.store';
import { frDay, frShort, frWeekday, money, qtyFr } from '../rental-days';
import { deliveredDay, returnedDay, sameDecisionTargets, sameMemoLineIds } from '../rental-planning.geometry';
import { LineDecisionRef, RentalLineDecisionsComponent, decisionSummary, decisionTitle, findDecision } from './rental-line-decisions.component';
import { memoTwins, prescriptionTwinKey, windowTwinKey } from '../decisions/rental-decision-dialog.base';
import { RentalLineLedgerComponent, carriedTexts, pieceLink, pieceRef, ranksText, spanText } from './rental-line-ledger.component';

export type SheetSection = NonNullable<LineSheetDialogData['section']>;

interface Fact { label: string; day: string; }
interface DueRow { ranks: string; span: string; qty: string; pu: string; ht: string; ttc: string; lpp: string | null; notes: string[]; }
interface CreditRow { ref: string; link: number | null; ranks: string; span: string; qty: string; ht: string; ttc: string; error: string | null; }
interface ParamRow { label: string; value: string; raw: string; }

export interface SheetView {
  line: RentalLineView;
  title: string; // intitulé du contrat de location, l'article passe dans sub
  asOf: Day;
  status: string; statusCls: string; interrupted: boolean;
  closure: string | null; // infobulle de la pastille « close » (2107 en vigueur) ; null = ligne ouverte
  sub: string[]; // ligne, article, mémo et période
  progress: { text: string; title: string | null }[]; // rang, facturé jusqu'au, prochaine échéance
  facts: Fact[];
  siblings: { id: number; name: string }[];
  lock: string | null; // décisions impossibles sur cette ligne
  startLock: string | null;
  due: DueRow[]; credits: CreditRow[];
  totals: { label: string; ht: string; ttc: string; cls: string }[];
  incomplete: string | null;
  card: ParamRow[]; cardMissing: boolean;
  anomalies: string[]; blocked: boolean;
  counts: Record<SheetSection, number | null>;
}

export const SHEET_SECTIONS: { key: SheetSection; label: string }[] = [
  { key: 'decisions', label: 'Décisions' },
  { key: 'ledger', label: 'Facturé' },
  { key: 'due', label: 'Au prochain passage' },
  { key: 'card', label: 'Contrat de location' },
  { key: 'anomalies', label: 'Anomalies' },
];

// Section Carte seulement (E.1) : traduction des paramètres, aucune conséquence de facturation n'en est tirée.
function cardRows(l: RentalLineView, store: RentalLifecycleStore): ParamRow[] {
  const rows: ParamRow[] = [
    { label: 'Contrat de location', value: l.terms_name || 'Contrat de location sans nom', raw: `terms_name = ${l.terms_name ?? 'null'}` },
    { label: 'Période', value: store.periodName(l.period) ?? (l.period == null ? 'Inconnue' : `période ${l.period}`), raw: `period = ${l.period ?? 'null'} · unit = ${l.unit ?? 'null'}` },
    { label: 'Durée', value: l.duration != null && l.duration > 0 ? `${l.duration} rang(s)` : 'Sans limite', raw: `duration = ${l.duration ?? 'null'}` },
  ];
  const p: RentalBillingParams | null = l.params;
  if (!p) return rows;
  const yes = (k: keyof typeof CARD_FLAG_LABELS) => CARD_FLAG_LABELS[k][p[k] ? 0 : 1];
  rows.push({ label: 'Interruption', value: INTERRUPTION_RULE_LABELS[p.interruption_rule] ?? p.interruption_rule, raw: `interruption_rule = ${p.interruption_rule}` });
  if (p.pause_close_after_days != null) {
    rows.push({ label: 'Clôture de la pause', value: `après ${p.pause_close_after_days} jour(s)`, raw: `pause_close_after_days = ${p.pause_close_after_days}` });
  }
  rows.push(
    { label: 'Fin de location', value: END_RULE_LABELS[p.end_rule] ?? p.end_rule, raw: `end_rule = ${p.end_rule}` },
    { label: 'Jour de reprise', value: yes('return_day_billed'), raw: `return_day_billed = ${p.return_day_billed}` },
    { label: 'Trop-facturé', value: OVERBILLING_LABELS[p.overbilling] ?? p.overbilling, raw: `overbilling = ${p.overbilling}` },
    { label: 'Avant le BL', value: yes('start_before_delivery'), raw: `start_before_delivery = ${p.start_before_delivery}` },
    { label: 'Prescription', value: yes('prescription_required'), raw: `prescription_required = ${p.prescription_required}` },
    { label: 'Prix', value: PRICE_SOURCE_LABELS[p.price_source] ?? p.price_source, raw: `price_source = ${p.price_source}` },
  );
  return rows;
}

// Tout ce que montre la fiche, lu dans la vue du serveur ; toutes les plages y sont start / end, premier et dernier jour inclus (0.2).
export function sheetViewOf(store: RentalLifecycleStore, lineId: number): SheetView | null {
  const v = store.view;
  const l = v?.lines.find((x) => x.id === lineId);
  if (!v || !l) return null;
  const all = store.rentalLines();
  const docTypeOf = (invoiceId: number) => l.ledger.find((e) => e.invoice_id === invoiceId)?.doc_type ?? v.invoices.find((i) => i.id === invoiceId)?.doc_type ?? null;
  const facts: Fact[] = [];
  if (l.realdatdel) facts.push({ label: 'Livré le', day: frDay(deliveredDay(l)) });
  else if (l.planned_on) facts.push({ label: 'Livraison prévue le', day: frDay(l.planned_on) });
  if (l.return_realdatdel) facts.push({ label: 'Repris le', day: frDay(returnedDay(l)) });
  else if (l.planned_return_on) facts.push({ label: 'Reprise prévue le', day: frDay(l.planned_return_on) });
  const lock = l.lifecycle_unreadable || !l.decisions ? 'Cycle de vie illisible : aucune décision possible sur cette ligne'
      : l.id <= 0 ? 'Ligne pas encore enregistrée' : lineClosed(l) ? CLOSED_LINE : null;
  const startLock = lock ?? (!l.realdatdel ? 'Ligne pas encore livrée'
    : l.params?.start_before_delivery !== true ? 'Le contrat de location n\'autorise pas de facturer avant le BL' : null);
  const d = l.decisions;
  const a = l.amounts;
  return {
    line: l,
    title: rentalTitle(l) ?? `Article ${l.product}`,
    asOf: v.as_of,
    status: statusLabel(l.status),
    statusCls: statusClass(l.status),
    interrupted: showInterruptedBadge(l, v.as_of),
    closure: lineClosed(l) ? closureTip(l.closure!) : null,
    sub: [`Ligne ${l.seq}`, rentalSubtitle(l), l.memo?.trim() || null, l.terms_name ? null : 'Sans contrat de location lisible', store.periodName(l.period)].filter((s): s is string => !!s),
    progress: [
      { text: `rang ${l.rank}/${l.duration != null && l.duration > 0 ? l.duration : '∞'}`, title: 'Rang atteint / Durée' },
      l.billed_until ? { text: `facturé jusqu'au ${frShort(l.billed_until)}`, title: `Dernier jour facturé, inclus : ${frDay(l.billed_until)}` } : { text: 'rien de facturé', title: null },
      ...(l.summary?.next_due_on ? [{ text: `prochaine échéance ${frWeekday(l.summary.next_due_on)}`, title: frDay(l.summary.next_due_on) }] : []),
    ],
    facts,
    siblings: sameMemoLineIds(l, all).filter((id) => id !== l.id).map((id) => ({ id, name: all.find((x) => x.id === id)?.product_name ?? `ligne ${id}` })),
    lock,
    startLock,
    due: (l.due ?? []).map((e) => ({
      ranks: ranksText(e.rank_start, e.rank_end), span: spanText(e.start, e.end), qty: qtyFr(e.qty), pu: money(e.uprice_taxx),
      ht: money(e.net_taxx), ttc: money(e.net_taxi), lpp: e.lpp ? `LPP ${e.lpp}` : null, notes: carriedTexts(e.carried, l.ledger ?? []),
    })),
    credits: (l.credits ?? []).map((c) => ({
      ref: c.corrects_doc_ref || pieceRef({ invoice_id: c.corrects_invoice_id, doc_type: docTypeOf(c.corrects_invoice_id), doc_ref: null }),
      link: pieceLink(c.corrects_invoice_id),
      ranks: ranksText(c.rank_start, c.rank_end), span: spanText(c.start, c.end), qty: qtyFr(c.qty),
      ht: c.net_taxx == null ? '' : money(c.net_taxx), ttc: c.net_taxi == null ? '' : money(c.net_taxi), error: c.error,
    })),
    totals: [
      { label: 'À facturer', ht: money(a.due_ht), ttc: money(a.due_ttc), cls: 'due' },
      { label: 'Avoirs', ht: money(a.credit_ht), ttc: money(a.credit_ttc), cls: 'cred' },
      { label: 'Total de la ligne', ht: money(a.net_ht), ttc: money(a.net_ttc), cls: 'net' },
    ],
    incomplete: a.complete ? null : 'Non chiffré en entier : ' + (a.blocking.length ? a.blocking.join(' · ') : 'ligne bloquée'),
    card: cardRows(l, store),
    cardMissing: !l.params,
    anomalies: l.anomalies ?? [],
    blocked: l.blocked,
    counts: {
      decisions: d ? d.interruptions.length + d.window.length + d.prescriptions.length : null,
      ledger: (l.ledger ?? []).length,
      due: (l.due ?? []).length + (l.credits ?? []).length,
      card: null,
      anomalies: (l.anomalies ?? []).length,
    },
  };
}

// Fiche d'une ligne RENTAL : décisions, registre, prochain passage, carte et anomalies ; ouvre les dialogues de décision (E.10).
@Component({
  selector: 'app-rental-line-sheet',
  templateUrl: './rental-line-sheet.component.html',
  styleUrls: ['./rental-line-sheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatDialogModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule, RouterLink, RentalLineDecisionsComponent, RentalLineLedgerComponent],
})
export class RentalLineSheetComponent {
  readonly data: LineSheetDialogData = inject(MAT_DIALOG_DATA);
  readonly dialogRef: MatDialogRef<RentalLineSheetComponent, DecisionDialogResult> = inject(MatDialogRef);
  private dialogs = inject(RentalDecisionDialogsService);
  private cdr = inject(ChangeDetectorRef);
  private host: ElementRef<HTMLElement> = inject(ElementRef);
  private injector = inject(Injector);
  private destroyRef = inject(DestroyRef);

  readonly sections = SHEET_SECTIONS;
  readonly badge = INTERRUPTED_BADGE;
  readonly store: RentalLifecycleStore = this.data.store;
  lineId = this.data.lineId; // une voisine de même mémo peut prendre la place, sans rouvrir de dialogue
  active: SheetSection | null = this.data.section ?? null;
  vm: SheetView | null = null;
  private pendingSection: SheetSection | null = this.data.section ?? null;
  private result: DecisionDialogResult = { saved: 0, failed: false };

  constructor() {
    // Échap et clic à côté rendent aussi le bilan des décisions prises depuis la fiche.
    this.dialogRef.disableClose = true;
    this.dialogRef.backdropClick().pipe(takeUntilDestroyed()).subscribe(() => this.close());
    this.dialogRef.keydownEvents().pipe(filter((e) => e.key === 'Escape'), takeUntilDestroyed()).subscribe(() => this.close());
    this.store.changed$.pipe(takeUntilDestroyed()).subscribe(() => this.refresh());
    this.refresh();
  }

  close(): void {
    this.dialogRef.close({ ...this.result });
  }

  retry(): void {
    this.store.reload();
  }

  showLine(id: number): void {
    this.lineId = id;
    this.pendingSection = null;
    this.active = null;
    this.refresh();
    afterNextRender(() => this.content()?.scrollTo({ top: 0 }), { injector: this.injector });
  }

  goTo(s: SheetSection): void {
    this.active = s;
    this.cdr.markForCheck();
    this.host.nativeElement.querySelector<HTMLElement>(`[data-section="${s}"]`)?.scrollIntoView({ block: 'start', behavior: 'auto' });
  }

  // Interruption, geste commercial compris (motif « Geste commercial », aux jours offerts) : plus de bouton Geste depuis le 27/10/2026.
  openInterruption(): void {
    this.track(this.dialogs.openRange({ store: this.store, orderId: this.data.orderId, origin: 'sheet', lineIds: [this.lineId] }));
  }

  openWindow(bound: 'START' | 'END'): void {
    this.track(this.dialogs.openWindow({ store: this.store, orderId: this.data.orderId, bound, lineIds: [this.lineId] }));
  }

  openPrescription(): void {
    this.track(this.dialogs.openPrescription({ store: this.store, orderId: this.data.orderId, lineId: this.lineId }));
  }

  onModify(ref: LineDecisionRef): void {
    const l = this.vm?.line;
    const x = findDecision(l, ref);
    if (!l || !x || x.cancelled) return;
    if (ref.typ === EventType.RENTAL_INTERRUPTION) {
      const i = x as RentalInterruption;
      // Les entrées identiques des voisines sont modifiées ensemble : leurs lignes restent cochées, sinon le dialogue les annulerait.
      const edit: RangeEditTarget[] = [{ lineId: l.id, id: i.id }, ...sameDecisionTargets(this.store.rentalLines(), l.id, i.id)];
      this.track(this.dialogs.openRange({
        store: this.store, orderId: this.data.orderId, origin: 'sheet',
        lineIds: [...new Set(edit.map((e) => e.lineId))], start: i.start, end: i.end, edit,
      }));
    } else if (ref.typ === EventType.RENTAL_PRESCRIPTION) {
      this.track(this.dialogs.openPrescription({ store: this.store, orderId: this.data.orderId, lineId: l.id, replaceId: x.id }));
    }
  }

  onCancel(ref: LineDecisionRef): void {
    const l = this.vm?.line;
    const x = findDecision(l, ref);
    if (!l || !x || x.cancelled) return;
    const lines = this.store.rentalLines();
    // Entrées identiques des autres lignes du même mémo, décochées (E.9), quel que soit le type : mêmes clés que les dialogues.
    const others: CancelTarget[] = ref.typ === EventType.RENTAL_INTERRUPTION
      ? sameDecisionTargets(lines, l.id, x.id).map((t) => ({ ...t, checked: false }))
      : ref.typ === EventType.RENTAL_WINDOW
        ? memoTwins(lines, l.id, x.id, (y) => y.decisions?.window, windowTwinKey)
        : ref.typ === EventType.RENTAL_PRESCRIPTION
          ? memoTwins(lines, l.id, x.id, (y) => y.decisions?.prescriptions, prescriptionTwinKey)
          : [];
    const targets: CancelTarget[] = [{ lineId: l.id, id: x.id, checked: true }, ...others];
    const who = `ligne ${l.seq}, ${l.product_name ?? 'article ' + l.product}` + (l.memo?.trim() ? ` (${l.memo.trim()})` : '');
    this.track(this.dialogs.openCancelDecision({
      store: this.store, orderId: this.data.orderId, typ: ref.typ, targets,
      title: decisionTitle(ref, x, this.store), summary: `${decisionSummary(ref, x, this.store)} · ${who}`,
    }));
  }

  private track(o: Observable<DecisionDialogResult>): void {
    o.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((r) => {
      this.result = { saved: this.result.saved + (r?.saved ?? 0), failed: this.result.failed || !!r?.failed };
    });
  }

  private refresh(): void {
    this.vm = sheetViewOf(this.store, this.lineId);
    this.cdr.markForCheck();
    if (this.vm && this.pendingSection) {
      const s = this.pendingSection;
      this.pendingSection = null;
      afterNextRender(() => this.goTo(s), { injector: this.injector });
    }
  }

  private content(): HTMLElement | null {
    return this.host.nativeElement.querySelector<HTMLElement>('.content');
  }
}
